import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pickLocalized } from '../../utils/testUtils';
import {
  bindInterestIntroKeyboard,
  interestIntroHintFromKey,
  shouldHandleInterestResponseKey
} from '../../utils/interestTestKeyboard';
import InterestClusterTileGrid from '../widgets/InterestClusterTileGrid';
import AlgorithmResultsDisclaimer from './AlgorithmResultsDisclaimer';

const INTRO_HINT_PULSE_MS = 420;

const INTRO_HINT_STYLES = {
  agree: {
    base: 'border-emerald-100 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/50 dark:text-emerald-200',
    active: 'border-emerald-300 bg-emerald-200 text-emerald-900 scale-[0.985] dark:border-emerald-500 dark:bg-emerald-900/70 dark:text-emerald-50'
  },
  neutral: {
    base: 'border-sky-100 bg-sky-50 text-sky-800 dark:border-sky-800/60 dark:bg-sky-950/50 dark:text-sky-200',
    active: 'border-sky-300 bg-sky-200 text-sky-900 scale-[0.985] dark:border-sky-500 dark:bg-sky-900/70 dark:text-sky-50'
  },
  disagree: {
    base: 'border-rose-100 bg-rose-50 text-rose-800 dark:border-rose-800/60 dark:bg-rose-950/50 dark:text-rose-200',
    active: 'border-rose-300 bg-rose-200 text-rose-900 scale-[0.985] dark:border-rose-500 dark:bg-rose-900/70 dark:text-rose-50'
  }
};

function introHintButtonClass(hint, active) {
  const styles = INTRO_HINT_STYLES[hint];
  return `flex min-h-[6.75rem] w-full flex-col items-center justify-center rounded-lg border p-2 text-center transition-all duration-200 sm:p-3 ${
    active ? styles.active : styles.base
  }`;
}

function InterestSwipeSessionView({
  ConfirmDialog,
  t,
  lang,
  paper,
  usedDemoFallback,
  phase,
  canResume,
  handleContinueSession,
  handleBeginSessionClick,
  isBeginningSession = false,
  currentItem,
  isComplete,
  questionIndex,
  total,
  handleRestartClick,
  progressPercent,
  beginDrag,
  updateDrag,
  endDrag,
  interactionLocked,
  cardBorderClass,
  cardStyle,
  commitResponse,
  optionButtonClass,
  submitError,
  handleTryAgainClick,
  clusterScores,
  hasCompletedInterestTest = false,
  onShowResults,
  sidebarLayout = false,
  triGroup = null
}) {
  const [activeIntroHint, setActiveIntroHint] = useState(null);
  const introPulseTimerRef = useRef(null);
  const swipeFocusRef = useRef(null);

  const pulseIntroHint = useCallback((hint) => {
    setActiveIntroHint(hint);
    if (introPulseTimerRef.current) {
      clearTimeout(introPulseTimerRef.current);
    }
    introPulseTimerRef.current = window.setTimeout(() => {
      setActiveIntroHint(null);
      introPulseTimerRef.current = null;
    }, INTRO_HINT_PULSE_MS);
  }, []);

  useEffect(() => {
    if (phase !== 'intro') {
      return undefined;
    }

    return bindInterestIntroKeyboard(pulseIntroHint);
  }, [phase, pulseIntroHint]);

  useEffect(() => {
    if (phase !== 'swiping' || !currentItem || interactionLocked) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      swipeFocusRef.current?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [phase, currentItem?.question?.id, interactionLocked]);

  useEffect(
    () => () => {
      if (introPulseTimerRef.current) {
        clearTimeout(introPulseTimerRef.current);
        introPulseTimerRef.current = null;
      }
    },
    []
  );

  const handleSwipeRegionKeyDown = (e) => {
    if (!shouldHandleInterestResponseKey(e)) return;
    const hint = interestIntroHintFromKey(e.key);
    if (!hint) return;
    e.preventDefault();
    e.stopPropagation();
    if (interactionLocked) return;
    const value = hint === 'agree' ? 1 : hint === 'neutral' ? 0 : -1;
    commitResponse(value);
  };

  const topClusters = Array.isArray(clusterScores) ? clusterScores : [];
  const isTriGroup = Boolean(triGroup);
  const currentRound = triGroup?.currentRound ?? 1;
  const totalRounds = triGroup?.totalRounds ?? 3;
  const shellClass = sidebarLayout
    ? 'rounded-lg border border-edge bg-surface p-4 shadow-sm sm:p-5'
    : 'bg-canvas min-h-[60vh] p-4 md:p-8';
  const innerClass = sidebarLayout ? 'w-full' : 'max-w-3xl mx-auto';

  return (
    <>
      {ConfirmDialog}
      <div className={shellClass}>
        <div className={innerClass}>
          {usedDemoFallback && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm text-justify dark:bg-amber-950/40 dark:border-amber-800/60 dark:text-amber-100">
              {t('test.demoFallback')}
            </div>
          )}

          {phase === 'intro' && (
            <div className={sidebarLayout ? '' : 'bg-surface border border-edge rounded-xl shadow-sm p-6 md:p-8'}>
              <div className="mx-auto flex w-full max-w-md flex-col items-center text-center">
                {!sidebarLayout ? (
                  <>
                    <h1 className="text-2xl md:text-3xl font-bold text-ink font-montserrat mb-3">
                      {pickLocalized(paper.title, lang)}
                    </h1>
                    <p className="text-ink-muted mb-6 whitespace-pre-wrap text-justify">
                      {pickLocalized(paper.paperInstruction, lang)}
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-lg font-bold text-ink sm:text-xl">{t('interestTest.sidebarTitle')}</h2>
                    {isTriGroup ? (
                      <p className="mt-2 text-sm leading-6 text-ink-muted">
                        {t('interestTest.triGroupIntro', { rounds: totalRounds, perRound: 40 })}
                      </p>
                    ) : (
                      <p className="mt-2 text-sm leading-6 text-ink-muted">{t('interestTest.swipeHint')}</p>
                    )}
                    {isTriGroup ? (
                      <p className="mt-2 text-sm leading-6 text-ink-muted">{t('interestTest.swipeHint')}</p>
                    ) : null}
                  </>
                )}

                <div className="mt-5 w-full rounded-xl border border-edge bg-surface-muted p-3">
                  <div className="grid grid-cols-3 gap-3 text-xs sm:text-sm">
                    <button
                      type="button"
                      onClick={() => pulseIntroHint('agree')}
                      className={introHintButtonClass('agree', activeIntroHint === 'agree')}
                    >
                      {t('interestTest.definitelyWantToTry')}
                    </button>
                    <button
                      type="button"
                      onClick={() => pulseIntroHint('neutral')}
                      className={introHintButtonClass('neutral', activeIntroHint === 'neutral')}
                    >
                      {t('interestTest.willThinkAboutIt')}
                    </button>
                    <button
                      type="button"
                      onClick={() => pulseIntroHint('disagree')}
                      className={introHintButtonClass('disagree', activeIntroHint === 'disagree')}
                    >
                      {t('interestTest.notInMyLifetime')}
                    </button>
                  </div>
                </div>

                {!sidebarLayout ? (
                  <p className="mt-6 text-sm text-ink-muted">{t('interestTest.swipeHint')}</p>
                ) : null}

                <div className="mt-5 flex w-full flex-col gap-3">
                  {submitError && phase === 'intro' ? (
                    <p className="w-full text-sm text-danger">{submitError}</p>
                  ) : null}
                  {canResume ? (
                    <button
                      type="button"
                      onClick={handleContinueSession}
                      className="w-full rounded-full bg-primary px-6 py-2 text-primary-foreground hover:bg-primary-hover"
                    >
                      {t('interestTest.continueSession')}
                    </button>
                  ) : null}
                  {isTriGroup && triGroup?.canContinueNextRound && !canResume ? (
                    <button
                      type="button"
                      onClick={triGroup.handleContinueNextRound}
                      disabled={isBeginningSession}
                      className="w-full rounded-full bg-primary px-6 py-2 text-primary-foreground hover:bg-primary-hover disabled:opacity-60"
                    >
                      {t('interestTest.continueNextRound', {
                        round: triGroup.nextRound ?? triGroup.currentRound
                      })}
                    </button>
                  ) : null}
                  {!(isTriGroup && triGroup?.canContinueNextRound && !canResume) ? (
                    <button
                      type="button"
                      onClick={handleBeginSessionClick}
                      disabled={isBeginningSession}
                      className={`w-full rounded-full px-6 py-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                        canResume
                          ? 'bg-surface-muted text-ink hover:bg-edge'
                          : 'bg-primary text-primary-foreground hover:bg-primary-hover'
                      }`}
                    >
                      {isBeginningSession
                        ? t('interestTest.beginSessionLoading')
                        : hasCompletedInterestTest
                          ? t('interestTest.retakeTest')
                          : t('interestTest.beginSession')}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {phase === 'swiping' && currentItem && !isComplete && (
            <div className="space-y-4">
              <div className="flex justify-between items-center gap-3">
                <p className="text-sm font-medium text-ink-muted">
                  {isTriGroup
                    ? t('interestTest.triGroupActivityProgress', {
                        round: triGroup.currentRound,
                        totalRounds: triGroup.totalRounds,
                        current: questionIndex + 1,
                        total
                      })
                    : t('interestTest.activityProgress', {
                        current: questionIndex + 1,
                        total
                      })}
                </p>
                <button
                  type="button"
                  onClick={handleRestartClick}
                  className="text-sm font-medium text-link hover:text-link-hover"
                >
                  {t('interestTest.restart')}
                </button>
              </div>
              <div className="w-full bg-edge rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <div
                ref={swipeFocusRef}
                role="button"
                tabIndex={-1}
                onKeyDown={handleSwipeRegionKeyDown}
                onMouseDown={beginDrag}
                onMouseMove={updateDrag}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                onTouchStart={beginDrag}
                onTouchMove={updateDrag}
                onTouchEnd={endDrag}
                className={`relative z-20 select-none bg-surface border border-edge rounded-2xl shadow-md outline-none transition-[transform,box-shadow,border-color] duration-200 [-webkit-tap-highlight-color:transparent] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas ${
                  sidebarLayout ? 'p-5 min-h-[240px]' : 'p-6 md:p-10 min-h-[320px]'
                } ${
                  interactionLocked ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
                } ${cardBorderClass}`}
                style={{ WebkitTapHighlightColor: 'transparent', ...cardStyle }}
              >
                <p className="text-xs uppercase tracking-wide text-ink-subtle font-semibold mb-3">
                  {pickLocalized(currentItem.section.title, lang)}
                </p>
                <p className={`font-bold text-ink leading-snug text-justify ${
                  sidebarLayout ? 'text-xl' : 'text-2xl md:text-3xl'
                }`}>
                  {pickLocalized(currentItem.question.prompt, lang)}
                </p>
              </div>

              <div className="relative z-0 grid grid-cols-1 gap-3">
                <button
                  type="button"
                  disabled={interactionLocked}
                  onClick={() => commitResponse(-1)}
                  className={optionButtonClass(-1)}
                >
                  {t('interestTest.notInMyLifetime')}
                </button>
                <button
                  type="button"
                  disabled={interactionLocked}
                  onClick={() => commitResponse(0)}
                  className={optionButtonClass(0)}
                >
                  {t('interestTest.willThinkAboutIt')}
                </button>
                <button
                  type="button"
                  disabled={interactionLocked}
                  onClick={() => commitResponse(1)}
                  className={optionButtonClass(1)}
                >
                  {t('interestTest.definitelyWantToTry')}
                </button>
              </div>
            </div>
          )}

          {phase === 'complete' && (
            <div className={`text-center ${sidebarLayout ? '' : 'bg-surface border border-edge rounded-xl shadow-sm p-6 md:p-8'}`}>
              <h2 className="text-xl font-bold text-ink mb-3">
                {isTriGroup && triGroup?.allRoundsComplete
                  ? t('interestTest.triGroupAllCompleteTitle')
                  : isTriGroup
                    ? t('interestTest.triGroupRoundCompleteTitle', {
                        round: triGroup?.completedRounds?.length || currentRound
                      })
                    : t('interestTest.completeTitle')}
              </h2>
              <p className="text-sm text-ink-muted mb-4">
                {isTriGroup
                  ? triGroup?.allRoundsComplete
                    ? t('interestTest.triGroupAllCompleteDescription', {
                        count: triGroup?.scoringCount || 120
                      })
                    : t('interestTest.triGroupRoundCompleteDescription', {
                        count: triGroup?.scoringCount || 40
                      })
                  : t('interestTest.completeDescription')}
              </p>
              {sidebarLayout ? null : topClusters.length > 0 ? (
                <div className="mb-6 text-left max-w-lg mx-auto">
                  <h3 className="text-sm font-semibold text-ink mb-3">
                    {t('interestTest.resultsHeading')}
                  </h3>
                  <InterestClusterTileGrid clusters={topClusters} showCaption={false} />
                  <AlgorithmResultsDisclaimer variant="interest" className="mt-4" />
                </div>
              ) : !submitError ? (
                <p className="text-sm text-ink-subtle mb-6">{t('interestTest.resultsPending')}</p>
              ) : null}
              {submitError ? (
                <p className="text-sm text-warning mb-6">{submitError}</p>
              ) : null}
              {sidebarLayout &&
              onShowResults &&
              !submitError &&
              (!isTriGroup ||
                triGroup?.allRoundsComplete ||
                triGroup?.canContinueNextRound ||
                (triGroup?.completedRounds?.length || 0) > 0) ? (
                <button
                  type="button"
                  onClick={onShowResults}
                  className="mb-3 w-full rounded-full bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-sm transition hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-canvas"
                >
                  {t('interestTest.showResults')}
                </button>
              ) : null}
              <div className="flex flex-wrap justify-center gap-3">
                {isTriGroup && triGroup?.canContinueNextRound ? (
                  <button
                    type="button"
                    onClick={triGroup.handleContinueNextRound}
                    className="px-6 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary-hover"
                  >
                    {t('interestTest.continueNextRound', {
                      round: triGroup.nextRound ?? triGroup.currentRound
                    })}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    handleTryAgainClick();
                  }}
                  className={`px-6 py-2 rounded-full ${
                    isTriGroup && triGroup?.allRoundsComplete
                      ? 'bg-surface-muted text-ink hover:bg-edge'
                      : 'bg-primary text-primary-foreground hover:bg-primary-hover'
                  }`}
                >
                  {isTriGroup && triGroup?.allRoundsComplete
                    ? t('interestTest.startOver')
                    : isTriGroup && triGroup?.canContinueNextRound
                      ? t('interestTest.pauseForNow')
                      : t('interestTest.retakeTest')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default InterestSwipeSessionView;
